import * as React from 'react';

import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { type StatusStyleType } from '@europrocurement/l2d-modules/hooks/useStatusStyle';
import { DossierStatus } from '@europrocurement/l2d-modules/modules/Dossiers';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { computeComponentColors } from '@europrocurement/flexy-components';

type DashboardStatCardTotalBarItems = {
    id?: number | null;
    libelle?: string | null;
    childsCount?: number | null;
    key: string;
};

type DashboardStatCardTotalBarProps = {
    items: DashboardStatCardTotalBarItems[];
    title: string;
    icon?: IconProp;
    handleItemClick: (statutId?: number | null | undefined) => void;
    handleStyleStatus?: (statutId?: number | null | undefined) => StatusStyleType;
};

const DashboardStatCardTotalBar: React.FunctionComponent<DashboardStatCardTotalBarProps> =
    function ({ items, title, icon, handleItemClick, handleStyleStatus }) {
        const { palette } = useTheme();

        const colorMain = palette.primary.main;
        const iconGlassy =
            palette.icon?.primary?.glassyStyle ??
            computeComponentColors(colorMain, palette.mode).glassyStyle;

        const totalItems = React.useMemo(
            () => items.reduce((sum, item) => sum + (item.childsCount ?? 0), 0),
            [items],
        );

        const rightFadeSx = {
            position: 'relative' as const,
            overflow: 'hidden',
            '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: 0,
                boxShadow: `0 0 20px 16px ${palette.background.paper}`,
                pointerEvents: 'none',
            },
        };

        const bottomFadeSx = {
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                bottom: '-5px',
                left: 0,
                right: 0,
                height: 0,
                boxShadow: `0 0 10px 14px ${palette.background.paper}`,
                pointerEvents: 'none',
                zIndex: 1,
            },
        };

        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                    {icon && (
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '10px',
                                ...iconGlassy,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                lineHeight: 0,
                            }}
                        >
                            <FaOptionIcon
                                icon={icon}
                                size="lg"
                                color={colorMain}
                            />
                        </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                display: 'block',
                                fontSize: '26px',
                                fontWeight: 600,
                                lineHeight: 1.2,
                                color: 'text.primary',
                            }}
                        >
                            {totalItems}
                        </Typography>
                        <Box sx={rightFadeSx}>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: 'text.secondary',
                                    lineHeight: 1.4,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {title}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        height: '8px',
                        mb: 1.5,
                    }}
                >
                    {items.map((item) => {
                        const percentage =
                            totalItems > 0 ? ((item.childsCount ?? 0) / totalItems) * 100 : 0;

                        const rawColor = handleStyleStatus
                            ? handleStyleStatus(item.id as number).color
                            : undefined;
                        const barColor = rawColor ? lighten(rawColor, 0.4) : undefined;

                        return (
                            <Tooltip
                                title={item.libelle}
                                arrow
                                placement="top"
                                key={`status_bar_${item.key}`}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        minHeight: '8px',
                                        width: `${percentage}%`,
                                        backgroundColor: barColor,
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>

                <Box
                    sx={{
                        ...rightFadeSx,
                        ...bottomFadeSx,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        pb: 1.5,
                        mb: -0.5,
                    }}
                >
                    {items.map((item) => {
                        if (item.childsCount && item.childsCount > 0) {
                            return (
                                <Tooltip
                                    title={item.libelle}
                                    arrow
                                    placement="top"
                                    key={`status_total_${item.key}`}
                                >
                                    <Box
                                        onClick={() => handleItemClick(item.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <DossierStatus
                                            label={item.childsCount.toString()}
                                            statutId={item.id as number}
                                            handleStyleStatus={handleStyleStatus}
                                            source="BACK"
                                        />
                                    </Box>
                                </Tooltip>
                            );
                        }
                        return <React.Fragment key={`status_total_empty_${item.id}`} />;
                    })}
                </Box>
            </>
        );
    };

export default DashboardStatCardTotalBar;
